import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { canEditSecretLevels } from '@infras/shared-types';
import { Pager, usePaged } from '../components/Pager';
import { LevelBadge, LevelSelect } from '../components/SecretLevel';
import { SecretNotes } from '../components/SecretNotes';
import { KeyNoteCell } from '../components/KeyNoteCell';
import { useMe } from '../lib/useMe';
import { normalizeEnv, orderEnvs } from '../lib/envs';
import { formatDate } from '../lib/format';
import { useCrumb } from '../lib/crumb';
import { CopyButton } from '../components/CopyButton';
import { DetailHead } from '../components/DetailHead';
import { Icon } from '../components/Icon';
import { RefreshButton } from '../components/RefreshButton';

const SECRET = gql`
  query Secret($name: String!) {
    secret(name: $name) {
      name
      isBundle
      level
      notes
      notesSetBy
      notesSetAt
      instances {
        id
        provider
        location
        env
        level
        keyNames
        createdTime
        labels
        sourceScanner
        firstSeenAt
        lastSeenAt
      }
      keys {
        keyName
        envs
        note
      }
    }
  }
`;

interface Instance {
  id: string;
  provider: string;
  location?: string;
  env?: string;
  level: string;
  keyNames: string[];
  createdTime?: string;
  labels?: string;
  sourceScanner: string;
  firstSeenAt: string;
  lastSeenAt: string;
}
interface KeyEnv {
  keyName: string;
  envs: string[];
  note?: string;
}
interface SecretDetail {
  name: string;
  isBundle: boolean;
  level: string;
  notes?: string;
  notesSetBy?: string;
  notesSetAt?: string;
  instances: Instance[];
  keys: KeyEnv[];
}
interface Data {
  secret: SecretDetail | null;
}

const envLabel = (i: Instance) => i.env ?? i.location ?? '?';

export function SecretDetailPage() {
  const { name = '' } = useParams();
  const [keyQ, setKeyQ] = useState('');
  const { data, loading, error, refetch } = useQuery<Data>(SECRET, {
    variables: { name },
    fetchPolicy: 'cache-and-network',
  });
  const { role } = useMe();
  const canEdit = !!role && canEditSecretLevels(role);

  const s = data?.secret;
  // Canonical deployment order (dev→prod), local folded to dev — matches /secrets.
  const envCols = useMemo(
    () => (s ? orderEnvs(s.instances.map((i) => normalizeEnv(envLabel(i)))) : []),
    [s],
  );
  const keys = (s?.keys ?? []).filter((k) => !keyQ || k.keyName.toLowerCase().includes(keyQ.toLowerCase()));
  const pg = usePaged(keys);

  // A logical secret spans several env instances. The WORST case is the useful
  // number here: the page shows every instance, so it is only as trustworthy as
  // the least recently confirmed one.
  const oldestScan = (s?.instances ?? [])
    .map((i) => i.lastSeenAt)
    .filter(Boolean)
    .sort()[0];

  // Breadcrumb tail — the topbar knows the route, not the entity. MUST sit above
  // the early returns: called after them it would be a conditional hook.
  useCrumb(s?.name);

  if (loading && !data) return <div className="page">Loading…</div>;
  if (error) return <div className="page muted">Failed to load: {error.message}</div>;
  if (!s)
    return (
      <div className="page">
        <p className="muted">Secret "{name}" not found.</p>
        <Link to="/secrets">← Back to secrets</Link>
      </div>
    );

  return (
    <div className="page">
      <div className="detail-back">
        <Link to="/secrets" className="muted">← Secrets</Link>
      </div>
      <DetailHead
        name={s.name}
        badge={<LevelBadge level={s.level} />}
        facts={[
          s.isBundle ? 'JSON bundle' : 'single-string',
          `${s.instances.length} env instance${s.instances.length === 1 ? '' : 's'}`,
          `${s.keys.length} key${s.keys.length === 1 ? '' : 's'}`,
        ]}
        scannedAt={oldestScan}
        scanNote="oldest of its env instances"
        actions={
          <>
            {/* The level SELECT is the one thing you act on here, so it belongs in
                the action slot. A first cut of this refactor dropped it while a
                comment claimed it had moved to a rail that was never built — root
                silently lost the ability to change a secret's level. */}
            {canEdit && (
              <LevelSelect name={s.name} level={s.level} onChanged={() => void refetch()} />
            )}
            <CopyButton value={s.name} />
            {/* Prefilled so the reviewer gets the exact secret, not a retyped
                approximation of it. A logical secret is one inventory row per
                env instance; link the prod one when there is one. */}
            {(() => {
              const inst =
                s.instances.find((i) => normalizeEnv(envLabel(i)) === 'prod') ?? s.instances[0];
              const q = inst
                ? `type=secret&entityId=${encodeURIComponent(inst.id)}&name=${encodeURIComponent(s.name)}`
                : `type=secret&name=${encodeURIComponent(s.name)}`;
              return (
                <Link className="btn btn-ghost" to={`/tasks/new?template=access&${q}`}>
                  Request access
                </Link>
              );
            })()}
          </>
        }
      />
      <p className="muted text-xs" style={{ marginTop: 8 }}>
        Names only — no secret <strong>values</strong> are stored. Key names are identifiers
        (a bundle's inner JSON keys, or a plain secret's last <code>--</code> segment).
      </p>

      <SecretNotes
        name={s.name}
        notes={s.notes}
        notesSetBy={s.notesSetBy}
        notesSetAt={s.notesSetAt}
        canEdit={canEdit}
        onChanged={() => void refetch()}
      />

      <div className="kv-grid">
        {(
          [
            ['Type', s.isBundle ? 'JSON bundle (--keys / -config)' : 'single-string'],
            ['Environments', s.instances.length],
            ['Distinct keys', s.keys.length],
            ['Provider(s)', [...new Set(s.instances.map((i) => i.provider))].join(', ')],
            ['Scanner', s.instances[0]?.sourceScanner ?? '—'],
          ] as [string, React.ReactNode][]
        ).map(([k, v]) => (
          <div className="kv" key={k}>
            <div className="kv-k">{k}</div>
            <div className="kv-v">{v}</div>
          </div>
        ))}
      </div>

      <div className="section-head">
        <h3>Environments ({s.instances.length})</h3>
        <span className="spacer" />
        <RefreshButton refetch={() => refetch().catch(() => undefined)} />
      </div>
      <table>
        <thead>
          <tr>
            <th>Env</th>
            <th>Project / location</th>
            <th>Secret name</th>
            <th>Keys</th>
            <th>Created</th>
            <th>Last seen</th>
          </tr>
        </thead>
        <tbody>
          {s.instances.map((i) => (
            <tr key={i.id}>
              <td>{i.env ?? '—'}</td>
              <td className="muted">{i.location ?? '—'}</td>
              <td className="muted text-2xs">{i.provider}</td>
              <td>{i.keyNames.length}</td>
              <td className="muted">{formatDate(i.createdTime)}</td>
              <td className="muted">{formatDate(i.lastSeenAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="section">Keys ({s.keys.length})</h3>
      {s.keys.length === 0 ? (
        <p className="muted">No keys recorded.</p>
      ) : (
        <>
          <div className="filter-row">
            <RefreshButton refetch={() => refetch().catch(() => undefined)} />
            <input placeholder="Filter keys…" value={keyQ} onChange={(e) => setKeyQ(e.target.value)} />
            <span className="filter-count">{keys.length} of {s.keys.length}</span>
          </div>
          <table className="mtx">
            <thead>
              <tr>
                <th>Key</th>
                {envCols.map((e) => (
                  <th key={e} className="env-col">{e}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pg.pageItems.map((k) => (
                <tr key={k.keyName}>
                  <td className="text-xs">
                    {k.keyName}
                    <KeyNoteCell keyName={k.keyName} note={k.note} canEdit={canEdit} onChanged={() => void refetch()} />
                  </td>
                  {envCols.map((e) => (
                    <td key={e} className="env-col">
                      {k.envs.some((x) => normalizeEnv(x) === e) ? (
                        <span className="mtx-yes"><Icon name="check" /></span>
                      ) : (
                        <span className="mtx-no">·</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={pg.page} pageCount={pg.pageCount} total={pg.total} start={pg.start} pageSize={pg.pageSize} onPage={pg.setPage} />
          {envCols.length > 1 && (
            <p className="muted text-2xs" style={{ marginTop: 8 }}>
              A key present in some envs but not others is config drift across environments.
            </p>
          )}
        </>
      )}
    </div>
  );
}
